import { useMemo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import NsContent from 'components/common/ns-content/NsContent';
import { v4 as uuidv4 } from 'uuid';

import { NsTitle } from 'components/common/ns-title/NsTitle';
import {
  setElectric,
  setGas,
  selectFuelType
} from 'state/fuel-type/fuel-type-slice';
import { getFuelValue } from 'components/direct-sales/fuel-type/getFuelValue';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { useNavigation } from 'hooks/useNavigation';
import { useHasInitialState } from 'components/direct-sales/personalise-quote/hooks/useHasInitialState';
import { setElectricVehicle } from 'state/electric-vehicle/electric-vehicle-slice';
import { setPersonaliseQuoteShowFuel } from 'state/settings/settings-slice';

import {
  FUEL_OPTIONS,
  PAYMENT_NAMES,
  PAYMENT_METHODS
} from 'config/app-config';

export const testId = 'mixedMetersTestId';

const radioButtonOptions = [
  {
    label: 'Electricity only',
    value: FUEL_OPTIONS.ELECTRIC
  },
  {
    label: 'Gas only',
    value: FUEL_OPTIONS.GAS
  }
];

export const MixedMeters = () => {
  const [key, setKey] = useState(uuidv4());
  const dispatch = useDispatch();

  const fuelType = useSelector(selectFuelType, shallowEqual);
  const fuelValue = getFuelValue(fuelType);
  const { eventTypes, next } = useNavigation();
  const { fuel: hasFuelInitialState } = useHasInitialState();

  const handleChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;

      if (value === FUEL_OPTIONS.ELECTRIC) {
        dispatch(setElectric());
      } else {
        dispatch(setGas());
        dispatch(setElectricVehicle(false));
      }
    },
    [dispatch]
  );

  const handleGoNext = useCallback(() => {
    next({ eventType: eventTypes.NEXT });
  }, [next, eventTypes.NEXT]);

  useEffect(() => {
    if (hasFuelInitialState) {
      dispatch(setElectric());
      dispatch(setPersonaliseQuoteShowFuel(false));

      setKey(uuidv4()); // refresh the radio input. bug in Nucleus
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => (
      <NsPanel testId={testId}>
        <NsBackCta />
        <NsContent>
          <NsTitle
            title={'Please get separate quotes for your Gas and Electricity'}
          />
          <p>
            It looks like you have one {PAYMENT_NAMES[PAYMENT_METHODS.PREPAY]}{' '}
            meter and one credit meter. So we need to give you separate quotes
            for each fuel. Please choose electricity or gas to continue.
          </p>
        </NsContent>
        <NsForm onSubmit={handleGoNext}>
          <NsRadioInput
            key={`mixedMeters${key}`}
            name="mixedMeters"
            dataTestId="radioMixedMetersTestId"
            onChange={handleChange}
            value={fuelValue}
            options={radioButtonOptions}
          />
          <NsPrimaryCta>Continue</NsPrimaryCta>
        </NsForm>
      </NsPanel>
    ),
    [handleChange, handleGoNext, fuelValue, key]
  );
};

export default MixedMeters;
